import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { WINDOW_TOKEN } from 'app/window-token';
import { LoggerService } from 'app/services/logger.service';
import { AuthType } from 'app/models/auth-type';
import { RAccountDetails } from 'app/records/account-details.record';
import { Endpoints } from 'app/services/endpoints';
import { environment } from 'environments/environment';

/* eslint-disable  @typescript-eslint/no-explicit-any */

@Injectable({
    providedIn: 'root'
})
export class InternalAuthInterceptor implements HttpInterceptor {
    private readonly oidc = inject(OidcSecurityService);
    private readonly window = inject<Window>(WINDOW_TOKEN);
    private readonly logger = inject(LoggerService).getScopedLogger('InternalAuthInterceptor');

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.logger.debug('Intercepting request:', req.url);

        const authType = this.getAuthTypeForUrl(req.url);

        if (!authType) {
            // No auth required, pass through
            return this.handleResponse(req, next.handle(req));
        }

        // Get token as an observable and clone the request.
        return from(this.oidc.getAccessToken(authType)).pipe(
            switchMap((token: string) => {
                this.logger.debug(`Setting token for ${authType}:`, token);
                const authReq = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${token}`
                    }
                });
                return this.handleResponse(authReq, next.handle(authReq));
            })
        );
    }

    private getAuthTypeForUrl(url: string): AuthType | null {
        if (url.startsWith(environment.adminApi)) {
            return AuthType.EMPLOYEE_FAC;
        } else if (url.startsWith(environment.provisioningApi)) {
            return AuthType.CUSTOMER_FORTICLOUD;
        }
        return null;
    }

    private handleResponse(
        request: HttpRequest<any>,
        response: Observable<HttpEvent<any>>
    ): Observable<HttpEvent<any>> {
        return response.pipe(
            map((event: HttpEvent<any>) => {
                if (
                    request.url.includes(Endpoints.USER) &&
                    event instanceof HttpResponse &&
                    event.body?.details
                ) {
                    const details: RAccountDetails = event.body.details;
                    this.logger.debug('USER endpoint response with details:', details);

                    if (!details.userAuthenticationPassed) {
                        this.logger.warn('User authentication failed.');

                        if (!details.userAuthenticationUrl) {
                            this.logger.error('Missing userAuthenticationUrl. Throwing error.');
                            throw new Error('User authentication URL not found.');
                        }

                        this.logger.warn('Redirecting to:', details.userAuthenticationUrl);
                        this.window.location.href = details.userAuthenticationUrl;
                    }
                }
                return event;
            }),
            catchError((err: any) => {
                if (err instanceof HttpErrorResponse) {
                    this.logger.error('HTTP error occurred:', err);

                    if (err.status === 401) {
                        this.logger.warn('Unauthorized (401). Initiating re-authentication.');

                        const authType = this.getAuthTypeForUrl(err.url ?? '');
                        if (authType) {
                            this.oidc.authorize(authType);
                        }
                    }
                } else {
                    this.logger.error('Unexpected error:', err);
                }

                return throwError(() => err);
            })
        );
    }
}
